import React, { useContext } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContexto";
import { FaArrowLeft } from "react-icons/fa";

import { usePedidoData } from "./crear/hooks/usePedidoData";
import { usePedidoCrear } from "./crear/hooks/usePedidoCrear";

import PedidoProductos from "./crear/components/PedidoProductos";
import PedidoTotalBar from "./crear/components/PedidoTotalBar";

function PedidoCrear() {
  const navigate = useNavigate();
  const { usuario } = useContext(AuthContext);

  // ✅ Datos (fetch separado)
  const { productos, mesas, meseros } = usePedidoData(usuario);

  // ✅ Lógica del pedido separada
  const {
    pedidoNuevo,
    setPedidoNuevo,
    agregarDetalle,
    actualizarDetalle,
    eliminarDetalle,
    manejarBusqueda,
    seleccionarProducto,
    total,
    mostrarPreview,
    setMostrarPreview,
    mostrarVistaPrevia,
    confirmarGuardarPedido,
    mostrarErrorInventario,
    setMostrarErrorInventario,
    mensajeErrorInventario,
  } = usePedidoCrear(usuario, productos, navigate);

  // UI helpers
  const meseroNombre =
    meseros.find((m) => m.id == pedidoNuevo.mesero)?.nombre || "—";

  return (
    <div className="mt-4 px-3 md:px-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white/95 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/70">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-amber-500 dark:text-amber-300 flex items-center gap-2">
              🧾 Crear nuevo pedido
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Selecciona tipo, mesa (si aplica) y agrega productos.
            </p>
          </div>

          <button
            onClick={() => navigate("/pedidos")}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-400 dark:border-slate-600 text-xs md:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <FaArrowLeft />
            Volver
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Datos generales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Tipo */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Tipo de pedido
              </label>
              <select
                value={pedidoNuevo.tipoPedido}
                onChange={(e) =>
                  setPedidoNuevo((prev) => ({
                    ...prev,
                    tipoPedido: e.target.value,
                    // si cambia de tipo, limpiamos mesa
                    mesa: e.target.value === "EN_MESA" ? prev.mesa : "",
                  }))
                }
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="EN_MESA">En Mesa</option>
                <option value="PARA_LLEVAR">Para Llevar</option>
                <option value="DOMICILIO">Domicilio</option>
              </select>
            </div>

            {/* Mesa */}
            {pedidoNuevo.tipoPedido === "EN_MESA" && (
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                  Mesa
                </label>
                <select
                  value={pedidoNuevo.mesa}
                  onChange={(e) =>
                    setPedidoNuevo((prev) => ({
                      ...prev,
                      mesa: e.target.value,
                    }))
                  }
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                >
                  <option value="">Seleccione una mesa</option>
                  {mesas.map((m) => (
                    <option key={m.numero} value={m.numero}>
                      Mesa {m.numero}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Mesero */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Mesero
              </label>
              <select
                value={pedidoNuevo.mesero}
                onChange={(e) =>
                  setPedidoNuevo((prev) => ({ ...prev, mesero: e.target.value }))
                }
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">Seleccione un usuario</option>
                {meseros.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Descripción general */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
              Descripción general
            </label>
            <textarea
              rows={2}
              placeholder="Ej: para llevar sin salsas…"
              value={pedidoNuevo.descripcion}
              onChange={(e) =>
                setPedidoNuevo((prev) => ({
                  ...prev,
                  descripcion: e.target.value,
                }))
              }
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/80 px-3 py-2 text-xs sm:text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          {/* Productos */}
          <PedidoProductos
            detalles={pedidoNuevo.detalles}
            productos={productos}
            onAdd={agregarDetalle}
            onUpdate={actualizarDetalle}
            onDelete={eliminarDetalle}
            onBuscar={manejarBusqueda}
            onSelectProducto={seleccionarProducto}
          />
        </div>
      </div>

      {/* ✅ Barra inferior móvil */}
      <PedidoTotalBar
        total={total}
        disabled={!pedidoNuevo.mesero || pedidoNuevo.detalles.length === 0}
        onPreview={mostrarVistaPrevia}
      />

      {/* ============ MODAL PREVIEW (provisional aquí, se extrae en paso 4) ============ */}
      {mostrarPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white dark:!bg-slate-900 text-slate-900 dark:text-slate-100 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 max-w-3xl w-[95%] max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:!bg-slate-800">
              <h3 className="text-sm md:text-base font-semibold flex items-center gap-2">
                📋 Vista previa del pedido
              </h3>
              <button
                onClick={() => setMostrarPreview(false)}
                className="text-xs px-2 py-1 rounded-lg border border-slate-400 dark:border-slate-600
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-700 dark:!text-slate-200
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition-colors"
              >
                Cerrar
              </button>
            </div>

            <div className="px-4 py-3 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm mb-3">
                <p>
                  <span className="font-semibold">Tipo: </span>
                  {pedidoNuevo.tipoPedido.replace("_", " ")}
                </p>
                {pedidoNuevo.tipoPedido === "EN_MESA" && (
                  <p>
                    <span className="font-semibold">Mesa: </span>
                    {pedidoNuevo.mesa || "—"}
                  </p>
                )}
                <p>
                  <span className="font-semibold">Mesero: </span>
                  {meseroNombre}
                </p>
                <p className="sm:col-span-2">
                  <span className="font-semibold">Descripción: </span>
                  {pedidoNuevo.descripcion || "—"}
                </p>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                <table className="min-w-full text-xs sm:text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-950/80">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">
                        Producto
                      </th>
                      <th className="px-3 py-2 text-center font-semibold">
                        Cant.
                      </th>
                      <th className="px-3 py-2 text-center font-semibold">
                        Precio
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Descripción
                      </th>
                      <th className="px-3 py-2 text-center font-semibold">
                        Subtotal
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidoNuevo.detalles.map((d, idx) => {
                      const prod = productos.find((p) => p.id == d.producto);
                      return (
                        <tr
                          key={idx}
                          className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40"
                        >
                          <td className="px-3 py-2">{prod?.nombre || "—"}</td>
                          <td className="px-3 py-2 text-center">{d.cantidad}</td>
                          <td className="px-3 py-2 text-center">
                            ${Number(d.precio || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2">{d.descripcion || "—"}</td>
                          <td className="px-3 py-2 text-center">
                            ${Number(d.subtotal || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 text-right text-sm font-semibold text-emerald-400">
                Total: ${Number(total || 0).toFixed(2)}
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 bg-slate-50 dark:!bg-slate-800">
              <button
                onClick={() => setMostrarPreview(false)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600
                          text-xs sm:text-sm
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-700 dark:!text-slate-200
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition-colors"
              >
                ✏️ Volver a editar
              </button>
              <button
                onClick={confirmarGuardarPedido}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-xs sm:text-sm font-semibold text-white transition-colors"
              >
                ✅ Confirmar pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL INVENTARIO (provisional aquí, se extrae en paso 4) ============ */}
      {mostrarErrorInventario && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white dark:!bg-slate-900 text-slate-900 dark:text-slate-100 rounded-2xl shadow-2xl border border-rose-300 dark:border-rose-700 max-w-lg w-[95%] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-rose-50 dark:!bg-slate-900">
              <h3 className="text-sm md:text-base font-semibold text-rose-600 dark:text-rose-300">
                ❌ Error de inventario
              </h3>
              <button
                type="button"
                onClick={() => setMostrarErrorInventario(false)}
                className="text-xs px-2 py-1 rounded-lg border border-slate-400 dark:border-slate-600 
                          !bg-slate-100 dark:!bg-slate-800 
                          !text-slate-700 dark:!text-slate-200 
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition"
              >
                Cerrar
              </button>
            </div>

            <div className="px-4 py-4 text-xs sm:text-sm space-y-2">
              <p className="text-slate-700 dark:text-slate-100 whitespace-pre-line">
                {mensajeErrorInventario ||
                  "No hay inventario suficiente para este pedido."}
              </p>
              <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400">
                Ajusta cantidades o revisa el stock de insumos.
              </p>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end bg-slate-50 dark:!bg-slate-900">
              <button
                type="button"
                onClick={() => setMostrarErrorInventario(false)}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg 
                          border border-slate-400 dark:border-slate-600
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-700 dark:!text-slate-200 
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PedidoCrear;
